import { Link } from 'react-router-dom';

interface ArticleCtaProps {
  category?: string;
  articleSlug?: string;
}

// Map blog slugs to auto broker city page routes
const CITY_BROKER_ROUTES: Record<string, { path: string; label: string }> = {
  'auto-broker-bend-oregon': { path: '/auto-broker-bend-or', label: 'Auto Broker Bend OR' },
  'auto-broker-eugene-oregon': { path: '/auto-broker-eugene-or', label: 'Auto Broker Eugene OR' },
  'auto-broker-medford-oregon': { path: '/auto-broker-medford-or', label: 'Auto Broker Medford OR' },
  'auto-broker-yakima-washington': { path: '/auto-broker-yakima-wa', label: 'Auto Broker Yakima WA' },
  'auto-broker-boise-idaho': { path: '/auto-broker-boise-id', label: 'Auto Broker Boise ID' },
  'auto-broker-denver-colorado': { path: '/auto-broker-denver-co', label: 'Auto Broker Denver CO' },
  'auto-broker-portland-oregon': { path: '/auto-broker-portland-or', label: 'Auto Broker Portland OR' },
  'auto-broker-seattle-washington': { path: '/auto-broker-seattle-wa', label: 'Auto Broker Seattle WA' },
};

export default function ArticleCta({ category, articleSlug }: ArticleCtaProps) {
  const cityRoute = articleSlug ? CITY_BROKER_ROUTES[articleSlug] : undefined;

  const getCtaContent = () => {
    if (category === 'City Broker Guide') {
      return {
        title: 'Ready to Skip the Dealership?',
        description: 'Tell us what you want and we\'ll source it from the wholesale market — delivered to your door at a price that beats any local dealer.',
        primaryText: 'Submit a Broker Inquiry',
        primaryLink: '/broker-services',
        secondaryText: 'Browse Inventory',
        secondaryLink: '/inventory',
        icon: 'ri-user-star-line',
      };
    }
    if (category === 'Financing Guide') {
      return {
        title: 'Ready to Get Pre-Approved?',
        description: 'Apply in minutes. All credit situations welcome — bad credit, no credit, first-time buyers.',
        primaryText: 'Apply for Financing',
        primaryLink: '/apply-for-financing',
        secondaryText: 'Learn About Financing',
        secondaryLink: '/financing',
        icon: 'ri-calculator-line',
      };
    }
    if (category === 'Comparisons') {
      return {
        title: 'See How We Compare',
        description: 'Wholesale pricing, zero dealer games, and delivery to your door. See why buyers choose Rockhound over CarMax, Carvana, and local dealers.',
        primaryText: 'Compare Dealers',
        primaryLink: '/compare',
        secondaryText: 'Browse Inventory',
        secondaryLink: '/inventory',
        icon: 'ri-scales-line',
      };
    }
    if (category === 'Buyer Guides' || category === 'Buyer Education') {
      return {
        title: 'Ready to Buy the Smart Way?',
        description: 'Browse our hand-selected inventory or let us source the exact vehicle you want at wholesale pricing.',
        primaryText: 'Browse Inventory',
        primaryLink: '/inventory',
        secondaryText: 'Broker Services',
        secondaryLink: '/broker-services',
        icon: 'ri-car-line',
      };
    }
    if (category === 'Dealership Comparison') {
      return {
        title: 'Ready to Skip the Dealership Games?',
        description: 'No hidden fees, no pressure, no markups. Just honest car buying the way it should be.',
        primaryText: 'Browse Inventory',
        primaryLink: '/inventory',
        secondaryText: 'How It Works',
        secondaryLink: '/how-it-works',
        icon: 'ri-shield-check-line',
      };
    }
    return {
      title: 'Have Questions? We\'re Here to Help',
      description: 'Contact our team for personalized assistance with your car buying journey.',
      primaryText: 'Contact Us',
      primaryLink: '/contact',
      secondaryText: 'Browse Inventory',
      secondaryLink: '/inventory',
      icon: 'ri-customer-service-line',
    };
  };

  const cta = getCtaContent();

  return (
    <div className="bg-gradient-to-br from-[#1C2B1E] to-[#2d4430] py-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-[#E8B84B] rounded-full mb-6">
          <i className={`${cta.icon} text-3xl text-white`}></i>
        </div>
        <h2 className="font-['Oswald'] text-3xl sm:text-4xl font-bold text-white mb-4">
          {cta.title}
        </h2>
        <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">
          {cta.description}
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <Link
            to={cta.primaryLink}
            className="px-8 py-4 bg-[#E8B84B] text-[#1C2B1E] rounded-full font-medium hover:bg-[#d4a43d] transition-colors inline-flex items-center gap-2 whitespace-nowrap cursor-pointer"
          >
            {cta.primaryText}
            <i className="ri-arrow-right-line"></i>
          </Link>
          <Link
            to={cta.secondaryLink}
            className="px-8 py-4 bg-white/10 text-white border-2 border-white/20 rounded-full font-medium hover:bg-white/20 transition-colors inline-flex items-center gap-2 whitespace-nowrap cursor-pointer"
          >
            {cta.secondaryText}
          </Link>
        </div>

        {/* City broker page deep link */}
        {cityRoute && (
          <div className="mt-8 pt-8 border-t border-white/20">
            <p className="text-sm text-white/60 mb-3">Looking for the dedicated city page?</p>
            <Link
              to={cityRoute.path}
              className="inline-flex items-center gap-2 text-[#E8B84B] font-semibold hover:text-white transition-colors text-sm"
            >
              <i className="ri-map-pin-2-line"></i>
              {cityRoute.label} — Full Service Page
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
